import { Accordion, Button, Spinner } from '@scale/scaleui';
import { useCallback, useEffect, useMemo } from 'react';
import { PROMPT_GENERATION } from 'react-scale/components/scalers/tasks/ThreadedInteraction/constants';
import { buildCandidatesWithId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import useChatCompletion from 'react-scale/hooks/chat/useChatCompletion';
import {
  CandidateMessageWithMetadata,
  ChatBenchmarkConfig,
  MessageRole,
  ReferenceText,
  Attachment,
} from '@scale/shared/types/chat';
import PromptCandidate from 'react-scale/components/scalers/tasks/ThreadedInteraction/PromptCandidate';
import pluralize from 'pluralize';
import {
  useChatHistory,
  useChatStoreExtract,
  useMostRecentResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useTemplateVariableStore } from 'react-scale/components/scalers/tasks/textcollection/stores/TemplateVariableStore';
import { logChatTaskEvent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/events';
import { ChatTaskEventType } from '@scale/shared/types/chat/events';
import jsonic from 'jsonic';
import { PopulatedProjectModelConfigDTO } from '@scale/customer-app-shared';

const fillTemplate = (template: string, variables: Record<string, string>) => {
  return template.replace(/{{(.*?)}}/g, (_v, key) => variables[key] ?? '');
};

type ModelPromptSelectorProps = {
  responseIndex: number;
  projectId?: string;
  chatBenchmarkConfig?: ChatBenchmarkConfig;
  referenceTexts: ReferenceText[];
  attachments?: Attachment[];
  isFirstTurn?: boolean;
  complete?: boolean;
  readOnly?: boolean;
  setSelectedPrompt: Function;
  setDisplaySuggestionText: Function;
};

const ModelPromptSelector = ({
  responseIndex,
  projectId,
  chatBenchmarkConfig,
  referenceTexts,
  attachments,
  isFirstTurn,
  complete,
  readOnly,
  setSelectedPrompt,
  setDisplaySuggestionText,
}: ModelPromptSelectorProps) => {
  const templateVariables = useTemplateVariableStore() || {};
  const chatConfig = useChatStoreExtract(state => state.config);
  const systemPrompt = useMemo(
    () => fillTemplate(chatConfig.promptGenerationSystemPrompt || '', templateVariables),
    [],
  );
  const mostRecentResponse = useMostRecentResponse(responseIndex);
  const isPreSeededPromptSuggestionEnabled = chatConfig.isPreSeededPromptSuggestionEnabled;
  const preSeededPromptSuggestions: Array<string> | null = useMemo(() => {
    // Pre-seeded prompts are only supported on the first turn
    if (!isPreSeededPromptSuggestionEnabled || !isFirstTurn) {
      return null;
    }
    const promptSuggestionsValue = fillTemplate(
      chatConfig.preSeededPromptSuggestions || '',
      templateVariables,
    );

    try {
      const parsedPromptSuggestions = jsonic(promptSuggestionsValue);
      if (Array.isArray(parsedPromptSuggestions)) {
        return parsedPromptSuggestions;
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  const projectModelConfigurationMapping = useChatStoreExtract(
    state => state.projectModelConfigurationMapping,
  );
  const setProjectModelConfigurationMapping = useChatStoreExtract(
    state => state.setProjectModelConfigurationMapping,
  );

  const chatModel = useMemo(() => {
    if (isPreSeededPromptSuggestionEnabled) {
      return null;
    }
    const model = PROMPT_GENERATION.MODELS.find(
      model => model.id === chatConfig.promptGenerationModel,
    );
    if (!model) {
      throw new Error('Prompt generation model not found');
    }
    return model;
  }, []);

  const chatHistory = useChatHistory(responseIndex);
  const chatHistoryWithoutSystemPrompt = useMemo(
    () => chatHistory.filter(message => message.role !== MessageRole.System),
    [responseIndex],
  );

  const chatCompletionHook = useChatCompletion({
    chatApiEndpoints: chatModel != null ? [chatModel] : [],
    providedModelResponses: [],
    projectId: projectId || null,
    useCase: 'chat-main model-prompt-selector',
    options: {
      chatBenchmarkConfig,
      templateVariables,
      projectModelConfigurationMapping,
    },
    responseIndex,
    setProjectModelConfigurationMapping: (
      projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>,
    ) => {
      setProjectModelConfigurationMapping(projectModelConfigurationMapping);
    },
  });
  const fetchCandidates = useCallback(() => {
    if (isPreSeededPromptSuggestionEnabled) {
      return;
    }
    const userMessage = attachments?.length
      ? 'generate prompts according to the attached image'
      : referenceTexts.length
      ? [
          ...referenceTexts.map(({ content }) => `Reference Text:\n${content}`),
          `Prompt:\n${PROMPT_GENERATION.USER_INPUT}`,
        ].join('\n')
      : PROMPT_GENERATION.USER_INPUT;

    const messages = [
      {
        role: MessageRole.System,
        content: `${systemPrompt || ''}\n

          ## Response format
          Return a JSON object and do not include any other text:
          {
            "prompts": string[]; // An array of prompt strings
          }
          `,
      },
      ...chatHistoryWithoutSystemPrompt,
      {
        role: MessageRole.Assistant,
        content: mostRecentResponse,
      },
    ].filter(message => message.content);

    chatCompletionHook.fetchCandidates(
      {
        role: MessageRole.User,
        content: userMessage,
        attachments,
      },
      messages,
      false,
      null,
      true,
    );
  }, [responseIndex, referenceTexts, attachments, mostRecentResponse]);

  useEffect(() => {
    if (complete) {
      return;
    }

    fetchCandidates();
  }, [complete, fetchCandidates]);

  const isLoading = isPreSeededPromptSuggestionEnabled
    ? false
    : !chatCompletionHook.areCandidatesFullyLoaded;

  const candidates = useMemo(() => {
    if (isPreSeededPromptSuggestionEnabled) {
      return [];
    }
    return chatCompletionHook.candidates;
  }, [chatCompletionHook]);

  // NOTE: A single candidate returns a list of prompts. We parse the candidate
  // and return a list of candidates with the same index and id but with different prompts
  // to reuse PromptCandidate component.
  const candidatesWithId: CandidateMessageWithMetadata[] = useMemo(() => {
    if (candidates.length === 0 || isLoading) {
      return [];
    }
    const candidatesWithId = buildCandidatesWithId(
      candidates,
      chatModel != null ? [chatModel] : [],
    );
    const candidateWithId = candidatesWithId[0];

    const parsePrompt = () => {
      const text = candidateWithId.message.content;
      const matches = text.match(/```json\s+(\{[\s\S]*?\})\s+```/);
      const value = matches ? matches[1] : text;
      try {
        return JSON.parse(value);
      } catch {
        return { prompts: [] };
      }
    };
    const prompts = parsePrompt().prompts;
    return prompts.map((prompt: string, index: number) => ({
      ...candidateWithId,
      message: {
        ...candidateWithId.message,
        content: prompt,
      },
      id: `${candidateWithId.id}-${index}`,
    }));
  }, [isLoading, candidates, chatModel]);

  if (readOnly) {
    return <></>;
  }

  return (
    <Accordion header="Prompt suggestions" startOpen>
      {isPreSeededPromptSuggestionEnabled && preSeededPromptSuggestions != null ? (
        <>
          <div
            className={`grid grid-cols-${preSeededPromptSuggestions.length > 1 ? '2' : '1'} gap-4`}
          >
            {preSeededPromptSuggestions.map((promptSuggestion: string, index: number) => (
              <PromptCandidate
                key={index}
                candidate={promptSuggestion}
                onCandidateClick={() => {
                  setDisplaySuggestionText(false);
                  setSelectedPrompt(promptSuggestion);
                  logChatTaskEvent({
                    type: ChatTaskEventType.PromptSuggestionsSelectPrompt,
                    metadata: { projectId: projectId || '' },
                    projectId,
                  });
                }}
              />
            ))}
          </div>
          <br />
        </>
      ) : (
        <>
          {isLoading && <Spinner size="xs" className="mb-4" />}
          {!isLoading && (
            <>
              <div className={`grid grid-cols-${candidatesWithId.length > 1 ? '2' : '1'} gap-4`}>
                {candidatesWithId.map(candidate => (
                  <PromptCandidate
                    key={candidate.id}
                    candidate={candidate}
                    onCandidateClick={() => {
                      setDisplaySuggestionText(false);
                      setSelectedPrompt(candidate.message.content);
                      logChatTaskEvent({
                        type: ChatTaskEventType.PromptSuggestionsSelectPrompt,
                        metadata: { projectId: projectId || '' },
                        projectId,
                      });
                    }}
                    errorButton={
                      <Button
                        disabled={chatCompletionHook.refetchCandidateCooldown > 0}
                        variant="warning"
                        size="sm"
                        onClick={async () => {
                          await fetchCandidates();
                        }}
                      >
                        {chatCompletionHook.refetchCandidateCooldown > 0
                          ? `Please wait ${pluralize(
                              'second',
                              chatCompletionHook.refetchCandidateCooldown,
                              true,
                            )}`
                          : 'Try Again'}
                      </Button>
                    }
                  ></PromptCandidate>
                ))}
              </div>
              <br />
            </>
          )}
        </>
      )}
    </Accordion>
  );
};

export default ModelPromptSelector;
